import { useMemo, type FC } from 'react';
import { IconClock } from '@tabler/icons-react';
import clsx from 'clsx';

interface Props {
  timeLeft: number
  totalTime?: number // Total time in seconds for progress calculation
}

const Countdown: FC<Props> = ({ timeLeft, totalTime }) => {
  const minutes = useMemo(() => Math.floor(timeLeft / 60), [timeLeft]);
  const seconds = useMemo(() => timeLeft % 60, [timeLeft]);

  // Format time as MM:SS
  const formattedTime = useMemo(() => {
    const mins = String(minutes).padStart(2, '0');
    const secs = String(seconds).padStart(2, '0');
    return `${mins}:${secs}`;
  }, [minutes, seconds]);

  // Calculate progress percentage (0-100)
  const progress = useMemo(() => {
    if (!totalTime || totalTime === 0) return 100;
    return Math.max(0, Math.min(100, (timeLeft / totalTime) * 100));
  }, [timeLeft, totalTime]);

  // Warning when less than 5 minutes left
  const isWarning = timeLeft < 300; // 5 minutes
  const isCritical = timeLeft < 60; // 1 minute

  // Calculate SVG circle progress
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={clsx(
      'relative inline-flex items-center gap-3 px-4 py-2.5 rounded-xl',
      'backdrop-blur-sm transition-all duration-300',
      'bg-white/90 border-2 border-gray-200 shadow-lg',
      'dark:bg-surface-dark/90 dark:border-borders dark:shadow-xl',
      // Warning state (less than 5 minutes)
      isWarning && !isCritical && 'border-amber-300 bg-amber-50/90 dark:border-amber-700 dark:bg-amber-900/30',
      // Critical state (less than 1 minute)
      isCritical && 'border-red-400 bg-red-50/90 dark:border-red-600 dark:bg-red-900/40',
      // Scale animation when critical
      isCritical && 'animate-pulse'
    )}>
      {/* Circular Progress Indicator */}
      <div className="relative flex-shrink-0">
        <svg
          width="64"
          height="64"
          className="transform -rotate-90"
          viewBox="0 0 64 64"
        >
          {/* Background circle */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            className={clsx(
              'transition-colors duration-300',
              isCritical
                ? 'text-red-100 dark:text-red-900/40'
                : isWarning
                  ? 'text-amber-100 dark:text-amber-900/40'
                  : 'text-gray-200 dark:text-gray-700'
            )}
          />
          {/* Progress circle */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={clsx(
              'transition-all duration-500 ease-linear',
              isCritical
                ? 'text-red-500 dark:text-red-400'
                : isWarning
                  ? 'text-amber-500 dark:text-amber-400'
                  : 'text-blue-500 dark:text-blue-400'
            )}
            style={{
              filter: isCritical ? 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.5))' : undefined
            }}
          />
        </svg>
        {/* Time display in center */}
        <div className={clsx(
          'absolute inset-0 flex items-center justify-center',
          'text-xs font-bold tabular-nums transition-colors duration-300'
        )}>
          <span className={clsx(
            isCritical
              ? 'text-red-600 dark:text-red-400'
              : isWarning
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-blue-600 dark:text-blue-400'
          )}>
            {minutes > 0 ? `${minutes}m` : `${seconds}s`}
          </span>
        </div>
      </div>

      {/* Time info */}
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <IconClock
            size={14}
            className={clsx(
              'flex-shrink-0 transition-colors duration-300',
              isCritical
                ? 'text-red-600 dark:text-red-400'
                : isWarning
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-blue-600 dark:text-blue-400'
            )}
          />
          <span className={clsx(
            'text-[10px] font-semibold uppercase tracking-wider transition-colors duration-300',
            isCritical
              ? 'text-red-600 dark:text-red-400'
              : isWarning
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-gray-500 dark:text-gray-400'
          )}>
            Tiempo restante
          </span>
        </div>
        <span className={clsx(
          'text-xl font-bold tabular-nums leading-none transition-colors duration-300',
          isCritical
            ? 'text-red-700 dark:text-red-300'
            : isWarning
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-900 dark:text-white'
        )}>
          {formattedTime}
        </span>
      </div>
    </div>
  );
};

export { Countdown };
